import type {
  AgentRun,
  ClaudeModel,
  EffortLevel,
  ItemAdvice,
  Lane,
  PlanContent,
  PlanItemInput,
  PlanProposal,
  SpecContent,
  WorkItem,
} from "@szyos/contracts";
import { CLAUDE_MODELS, EFFORT_LEVELS, LANES } from "@szyos/contracts";
import {
  EFFORT_INFO,
  LANE_LABELS,
  MODEL_INFO,
  SMALL_ORDER_MAX_MUST_HAVES,
  adviseRun,
  countRuns,
  displayTitle,
  isLargeSpec,
  projectFlow,
} from "@szyos/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useCallback, useId, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { api, ApiError, errorMessage, type AuthStatus, type WorkOrderDetail } from "../api";
import { DecisionCard } from "../components/DecisionCard";
import { SplitOrder } from "../components/SplitOrder";
import { StudioChat } from "../components/StudioChat";
import { WorkOrderTokens } from "../components/Usage";
import { EmptyState, ErrorNote, useToast } from "../components/feedback";
import { FlowMap, type FlowTarget } from "../components/FlowMap";
import { Activity, CostRing, Drawer, Stepper, Timeline, type CostRingRow } from "../components/widgets";
import { sameContent } from "../compare";
import { DEMO_PLAN, DEMO_SPEC } from "../demo";
import { useSimulation } from "../simulation";
import {
  AUTH_MODE_LABEL,
  LANE_ICON,
  STATE_LABEL,
  adapterLabel,
  orderStatus,
  readableReason,
  shortHash,
  untilText,
  usd,
} from "../format";
import { Icon } from "../icons";

const lines = (text: string) =>
  text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

function issuesText(err: unknown): string {
  if (err instanceof ApiError && Array.isArray(err.details)) {
    return (err.details as { path?: string; message: string }[])
      .map((i) => (i.path ? `${i.path}: ${i.message}` : i.message))
      .join(" · ");
  }
  if (err instanceof ApiError && err.details && typeof err.details === "object" && "issues" in err.details) {
    return ((err.details as { issues: { message: string }[] }).issues ?? [])
      .map((i) => i.message)
      .join(" · ");
  }
  return errorMessage(err);
}

/** Anbieter mit Zugang, z. B. „Claude Code (Abo-Modus)“; Mock ohne Zusatz. */
function runProvider(run: AgentRun): string {
  const mode = AUTH_MODE_LABEL[run.authMode];
  return mode ? `${adapterLabel(run.adapter)} (${mode})` : adapterLabel(run.adapter);
}

/** Kosten je Anbieter und Zugang; Abo-Läufe bleiben ohne Dollarbetrag (docs/03 §8). */
function costRowsOf(runs: AgentRun[]): CostRingRow[] {
  const groups = new Map<string, AgentRun[]>();
  for (const run of runs) {
    const key = `${run.adapter}|${run.authMode === "subscription" ? "subscription" : ""}`;
    groups.set(key, [...(groups.get(key) ?? []), run]);
  }
  return [...groups.values()].map((list) => ({
    adapter: list[0]!.adapter,
    authMode: list[0]!.authMode === "subscription" ? "subscription" : undefined,
    measuredUsd: list.reduce((s, r) => s + r.usage.costUsdMeasured, 0),
    estimatedUsd: list.reduce((s, r) => s + r.usage.costUsdEstimated, 0),
    quotaUnits: list.reduce((s, r) => s + r.usage.quotaUnits, 0),
    apiEquivalentUsd: list.reduce((s, r) => s + (r.usage.apiEquivalentUsd ?? 0), 0),
    runs: list.length,
  }));
}

/** Text für die Kostenzeile im Drawer: Dollar nur für Läufe außerhalb des Abos. */
function laneCostText(runs: AgentRun[]): string {
  const subscription = runs.filter((r) => r.authMode === "subscription").length;
  const paid = runs.filter((r) => r.authMode !== "subscription");
  const amount = `${usd(paid.reduce((s, r) => s + r.usage.costUsdMeasured, 0))} gemessen`;
  if (subscription === 0) return amount;
  const quota = `${subscription} ${subscription === 1 ? "Lauf" : "Läufe"} im Abo-Kontingent`;
  return paid.length === 0 ? quota : `${amount}, dazu ${quota}`;
}

/** Hinweis bei Anbieterpause oder Blockade mit Grund und Rücksetzzeit (ADR-016, ACC-37). */
function StateNotice({
  state,
  reason,
  pausedUntil,
}: {
  state: string;
  reason: string | null;
  pausedUntil: string | null;
}) {
  if (state === "PAUSED_PROVIDER") {
    return (
      <div className="state-notice is-paused" role="status">
        <Icon name="clock" />
        <div>
          <strong>Provider pausiert{pausedUntil ? `, weiter ab ${untilText(pausedUntil)}` : ""}</strong>
          {reason && <p>Grund: {readableReason(reason)}</p>}
          <p className="muted">
            szyOS setzt den Auftrag danach selbst fort, mit demselben Zugang. Ein stiller Wechsel auf den
            API-Schlüssel findet nicht statt.
          </p>
        </div>
      </div>
    );
  }
  if (state === "BLOCKED" && reason) {
    return (
      <div className="state-notice is-blocked" role="status">
        <Icon name="alert" />
        <div>
          <strong>Blockiert</strong>
          <p>Grund: {readableReason(reason)}</p>
        </div>
      </div>
    );
  }
  return null;
}

/* ---------- Spezifikation ---------- */

function SpecEditor({ detail, canApprove }: { detail: WorkOrderDetail; canApprove: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const wo = detail.workOrder;
  const initial = detail.spec?.content;
  const [problem, setProblem] = useState(initial?.problem ?? "");
  const [audience, setAudience] = useState(initial?.audience ?? "");
  const [mustHaves, setMustHaves] = useState(initial?.mustHaves.join("\n") ?? "");
  const [nonGoals, setNonGoals] = useState(initial?.nonGoals.join("\n") ?? "");
  const [designNotes, setDesignNotes] = useState(initial?.designNotes ?? "");
  const [acceptance, setAcceptance] = useState(initial?.acceptance.join("\n") ?? "");
  const [error, setError] = useState<string | null>(null);
  const simulation = useSimulation();
  const ids = { p: useId(), a: useId(), m: useId(), n: useId(), d: useId(), c: useId() };
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["work-order", wo.id] });

  const spec = (): SpecContent => ({
    problem: problem.trim(),
    audience: audience.trim(),
    mustHaves: lines(mustHaves),
    nonGoals: lines(nonGoals),
    designNotes: designNotes.trim(),
    acceptance: lines(acceptance),
  });
  const submit = useMutation({
    mutationFn: () => api.submitSpec(wo.id, spec(), wo.version),
    onSuccess: async () => {
      setError(null);
      toast("Spec-Revision gespeichert");
      await refresh();
    },
    onError: (err) => setError(issuesText(err)),
  });
  const approve = useMutation({
    mutationFn: () => api.approveSpec(wo.id, wo.specHash ?? "", wo.version),
    onSuccess: async () => {
      toast("Spec freigegeben. Weiter mit dem Plan.");
      await refresh();
    },
    onError: (err) => {
      // Zusätzlich als Meldung: auf dem Handy liegt das Formular weit über dem Knopf.
      setError(errorMessage(err));
      toast(errorMessage(err), "error");
    },
  });
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const s = spec();
    if (!s.problem || !s.audience || s.mustHaves.length === 0 || s.acceptance.length === 0) {
      setError("Problem, Zielgruppe, mindestens eine Muss-Funktion und ein Akzeptanzkriterium angeben.");
      return;
    }
    submit.mutate();
  };
  const suggested = detail.suggestedSpec;
  const adoptSuggested = () => {
    if (!suggested) return;
    setProblem(suggested.problem);
    setAudience(suggested.audience);
    setMustHaves(suggested.mustHaves.join("\n"));
    setNonGoals(suggested.nonGoals.join("\n"));
    setDesignNotes(suggested.designNotes);
    setAcceptance(suggested.acceptance.join("\n"));
    toast("Entwurf aus dem Planungsstudio übernommen. Prüfen, anpassen, dann speichern.");
  };
  const fillDemo = () => {
    setProblem(DEMO_SPEC.problem);
    setAudience(DEMO_SPEC.audience);
    setMustHaves(DEMO_SPEC.mustHaves.join("\n"));
    setNonGoals(DEMO_SPEC.nonGoals.join("\n"));
    setDesignNotes(DEMO_SPEC.designNotes);
    setAcceptance(DEMO_SPEC.acceptance.join("\n"));
  };

  return (
    <div className="stage stage--editor">
      <form className="panel form editor" onSubmit={onSubmit} noValidate aria-labelledby="specTitle">
        <div className="panel-head">
          <div>
            <h2 id="specTitle">Briefing und Spezifikation</h2>
            <p className="muted">
              Eine Zeile je Punkt. Jede gespeicherte Fassung ist eine eigene Revision mit Hash.
            </p>
          </div>
          <span className="plan-head-actions">
            {suggested && (
              <button type="button" className="chip-btn is-primary" onClick={adoptSuggested}>
                <Icon name="bot" /> Entwurf aus dem Planungsstudio übernehmen
              </button>
            )}
            {simulation && (
              <button type="button" className="chip-btn" onClick={fillDemo}>
                Beispiel einfügen
              </button>
            )}
          </span>
        </div>
        <div className="form-grid">
          <div>
            <label htmlFor={ids.p}>Welches Problem lösen wir?</label>
            <textarea id={ids.p} rows={3} value={problem} onChange={(e) => setProblem(e.target.value)} />
            <label htmlFor={ids.a}>Für wen?</label>
            <textarea id={ids.a} rows={2} value={audience} onChange={(e) => setAudience(e.target.value)} />
            <label htmlFor={ids.d}>Design und Look-and-feel</label>
            <textarea
              id={ids.d}
              rows={3}
              value={designNotes}
              onChange={(e) => setDesignNotes(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor={ids.m}>Muss-Funktionen</label>
            <textarea id={ids.m} rows={4} value={mustHaves} onChange={(e) => setMustHaves(e.target.value)} />
            <label htmlFor={ids.n}>Was die App nicht tun darf</label>
            <textarea id={ids.n} rows={2} value={nonGoals} onChange={(e) => setNonGoals(e.target.value)} />
            <label htmlFor={ids.c}>Akzeptanzkriterien</label>
            <textarea
              id={ids.c}
              rows={3}
              value={acceptance}
              onChange={(e) => setAcceptance(e.target.value)}
            />
          </div>
        </div>
        <ErrorNote error={error} />
        <div className="form-actions">
          <button type="submit" className="btn" disabled={submit.isPending}>
            <span className="btn-label">
              <Icon name="file" /> Revision speichern
            </span>
          </button>
        </div>
      </form>
      <aside className="panel decision" aria-label="Freigabe der Spezifikation">
        <div className="dec-top">
          <span className="dec-kicker">Freigabe</span>
        </div>
        {wo.state === "SPEC_REVIEW" && detail.spec ? (
          <>
            <h2 className="dec-title">
              <Icon name="file" /> <span>Spec v{detail.spec.revision} freigeben?</span>
            </h2>
            <p className="dec-text">
              Die Freigabe gilt genau für diese Revision. Jede spätere Änderung verlangt eine neue Freigabe.
            </p>
            {isLargeSpec(detail.spec.content) && (
              <p className="dec-text large-spec" role="note">
                <Icon name="alert" /> Großer Auftrag: {detail.spec.content.mustHaves.length} Muss-Funktionen,
                empfohlen sind höchstens {SMALL_ORDER_MAX_MUST_HAVES}. Kleinere Aufträge werden eher fertig.
                Überlege, den Rest mit „Auftrag teilen“ in einen zweiten Auftrag zu verschieben.
              </p>
            )}
            {canApprove && <SplitOrder wo={wo} spec={detail.spec.content} />}
            <ul className="binding">
              <li>
                <Icon name="commit" /> {shortHash(detail.spec.hash)}
              </li>
              <li>
                <Icon name="lock" /> Rolle owner
              </li>
            </ul>
            {canApprove ? (
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => approve.mutate()}
                disabled={approve.isPending}
              >
                <span className="btn-label">
                  Spec freigeben <Icon name="arrow" />
                </span>
              </button>
            ) : (
              <p className="muted">Nur der Owner kann die Spezifikation freigeben.</p>
            )}
          </>
        ) : (
          <EmptyState
            icon="file"
            title="Noch keine Revision"
            text="Speichere das Briefing. Danach kann der Owner diese Fassung freigeben."
          />
        )}
      </aside>
    </div>
  );
}

/* ---------- Plan ---------- */

interface DraftItem {
  key: string;
  lane: Lane;
  title: string;
  criteria: string;
  dependsOn: string[];
  provider: string;
  /** Leer heißt: Standard unter Agenten. */
  model: ClaudeModel | "";
  effort: EffortLevel | "";
}

/** Wer die Aufgabe bearbeitet; Claude Code nur auf einem Node mit Sandbox (M2). */
const PROVIDERS = [
  { value: "mock", label: "Mock-Agent (Probe)" },
  { value: "claude", label: "Claude Code" },
] as const;

function slug(text: string, taken: Set<string>): string {
  const base =
    text
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 30) || "aufgabe";
  let key = base;
  let n = 2;
  while (taken.has(key)) key = `${base}-${n++}`;
  return key;
}

function toDraft(plan: PlanContent | undefined): DraftItem[] {
  return (plan?.items ?? []).map((i) => ({
    key: i.key,
    lane: i.lane,
    title: i.title,
    criteria: i.acceptanceCriteria.join("\n"),
    dependsOn: i.dependsOn,
    provider: i.providerProfile,
    model: i.model ?? "",
    effort: i.effort ?? "",
  }));
}

/** Beratung je Aufgabe aus dem Planvorschlag der KI (nach Prüferin und Leitplanken), nach Schlüssel. */
type AiAdvice = Map<string, ItemAdvice>;

/**
 * Vorschlag der Beraterin für eine Planaufgabe: die geprüfte Wahl der KI, solange die Aufgabe noch so heißt wie im
 * Vorschlag; sonst die Regelberatung aus Aufgabe und freigegebenem Briefing.
 */
function adviceFor(item: DraftItem, spec: SpecContent | undefined, ai?: AiAdvice): ItemAdvice {
  const fromAi = ai?.get(`${item.key}\u0000${item.title.trim()}`);
  if (fromAi) return fromAi;
  return {
    ...adviseRun({
      lane: item.lane,
      title: item.title,
      acceptanceCriteria: lines(item.criteria),
      spec: spec ?? null,
    }),
    source: "regeln",
  };
}

const VERDICT_LABEL: Record<NonNullable<ItemAdvice["review"]>["verdict"], string> = {
  passt: "passt",
  zu_gross: "zu groß geplant",
  zu_klein: "zu knapp geplant",
};

/** Kurzes Urteil der Prüferin in der Vorschlagsliste, nur wenn sie etwas zu beanstanden hatte. */
function ReviewMark({ advice }: { advice: ItemAdvice | undefined }) {
  const verdict = advice?.review?.verdict;
  if (!verdict || verdict === "passt") return null;
  return <span className="muted"> · Prüferin: {VERDICT_LABEL[verdict]}</span>;
}

/** Beratung aus einem Planvorschlag, nach Schlüssel und Titel, damit umbenannte Aufgaben neu beraten werden. */
function aiAdviceOf(proposal: PlanProposal | null | undefined): AiAdvice {
  const map: AiAdvice = new Map();
  for (const item of proposal?.items ?? []) {
    const a = proposal?.advice?.[item.key];
    if (a) map.set(`${item.key}\u0000${item.title.trim()}`, a);
  }
  return map;
}

/** Modell und Aufwand einer Claude-Aufgabe, mit Vorschlag und Begründung der Beraterin. */
function ClaudeChoice({
  item,
  spec,
  ai,
  onChange,
}: {
  item: DraftItem;
  spec: SpecContent | undefined;
  ai: AiAdvice;
  onChange: (patch: Partial<DraftItem>) => void;
}) {
  const advice = adviceFor(item, spec, ai);
  const adopted = item.model === advice.model && item.effort === advice.effort;
  return (
    <div className="claude-choice">
      <div className="claude-choice-fields">
        <span>
          <label htmlFor={`model-${item.key}`}>Modell</label>
          <select
            id={`model-${item.key}`}
            value={item.model}
            onChange={(e) => onChange({ model: e.target.value as ClaudeModel | "" })}
          >
            <option value="">Standard unter Agenten</option>
            {CLAUDE_MODELS.map((m) => (
              <option key={m} value={m}>
                {MODEL_INFO[m].label}
              </option>
            ))}
          </select>
        </span>
        <span>
          <label htmlFor={`effort-${item.key}`}>Aufwand</label>
          <select
            id={`effort-${item.key}`}
            value={item.effort}
            onChange={(e) => onChange({ effort: e.target.value as EffortLevel | "" })}
          >
            <option value="">Standard unter Agenten</option>
            {EFFORT_LEVELS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </span>
      </div>
      <div className="advice" aria-label="Vorschlag der Beraterin">
        <p>
          <strong>{advice.source === "ki" ? "Beraterin (KI):" : "Beraterin (Regeln):"}</strong>{" "}
          {MODEL_INFO[advice.model].label}, Aufwand {advice.effort}{" "}
          <span className="muted">({EFFORT_INFO[advice.effort]})</span>
        </p>
        <ul>
          {advice.reasons.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
        {advice.review && (
          <p className="advice-review">
            <strong>Prüferin:</strong> {VERDICT_LABEL[advice.review.verdict]}
            {advice.review.removedCriteria.length > 0 && (
              <> · gestrichen: {advice.review.removedCriteria.map((c) => `„${c}“`).join(", ")}</>
            )}
          </p>
        )}
        {adopted ? (
          <span className="advice-adopted">
            <Icon name="check" /> übernommen
          </span>
        ) : (
          <button
            type="button"
            className="chip-btn"
            onClick={() => onChange({ model: advice.model, effort: advice.effort })}
          >
            Vorschlag übernehmen
          </button>
        )}
        {advice.recommendedEffort &&
          (item.effort === advice.recommendedEffort ? (
            <span className="advice-adopted">
              <Icon name="check" /> Aufwand {advice.recommendedEffort} eingeschaltet
            </span>
          ) : (
            <button
              type="button"
              className="chip-btn"
              onClick={() =>
                onChange({ model: advice.model, effort: advice.recommendedEffort ?? advice.effort })
              }
            >
              Empfehlung einschalten: Aufwand {advice.recommendedEffort}
            </button>
          ))}
      </div>
    </div>
  );
}

function PlanEditor({ detail, canApprove }: { detail: WorkOrderDetail; canApprove: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const wo = detail.workOrder;
  const [items, setItems] = useState<DraftItem[]>(() => toDraft(detail.plan?.content));
  const [error, setError] = useState<string | null>(null);
  // Im Betrieb nur echte Agenten; den Mock-Agenten gibt es nur in Tests und lokaler Entwicklung.
  const simulation = useSimulation();
  const providers = PROVIDERS.filter((p) => simulation || p.value !== "mock");
  // Welcher Planvorschlag schon übernommen ist; ein neuer Vorschlag macht den Knopf wieder aktiv.
  const [adoptedProposal, setAdoptedProposal] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["work-order", wo.id] });

  const plan = (): PlanContent => ({
    items: items.map((i): PlanItemInput => ({
      key: i.key,
      lane: i.lane,
      title: i.title.trim(),
      acceptanceCriteria: lines(i.criteria),
      dependsOn: i.dependsOn,
      providerProfile: i.provider,
      ...(i.provider === "claude" && i.model ? { model: i.model } : {}),
      ...(i.provider === "claude" && i.effort ? { effort: i.effort } : {}),
    })),
  });
  const spec = detail.spec?.content;
  const applyAdvice = () =>
    setItems((list) =>
      list.map((it) => {
        if (it.provider !== "claude") return it;
        const a = adviceFor(it, spec, aiAdvice);
        return { ...it, model: a.model, effort: a.effort };
      }),
    );
  const submit = useMutation({
    mutationFn: () => api.submitPlan(wo.id, plan(), wo.version),
    onSuccess: async () => {
      setError(null);
      toast("Plan-Revision gespeichert und als DAG geprüft");
      await refresh();
    },
    onError: (err) => setError(issuesText(err)),
  });
  const approve = useMutation({
    mutationFn: () => api.approvePlan(wo.id, wo.planHash ?? "", wo.version),
    onSuccess: async () => {
      toast("Plan freigegeben. Der Auftrag ist startbereit.");
      await refresh();
    },
    onError: (err) => {
      // Zusätzlich als Meldung: auf dem Handy liegt das Formular weit über dem Knopf.
      setError(errorMessage(err));
      toast(errorMessage(err), "error");
    },
  });
  // Planvorschlag der KI im Planungsstudio (M5-01), nur solange der Plan noch nicht freigegeben ist.
  const canAskPlan = wo.state === "PLAN_REVIEW";
  const studio = useQuery({
    queryKey: ["studio", "wo", wo.id],
    queryFn: () => api.studioSessions({ workOrderId: wo.id }),
    enabled: canAskPlan,
    refetchInterval: (q) => (q.state.data?.[0]?.state === "waiting_ai" ? 3000 : false),
  });
  const draftSession = studio.data?.[0];
  const planProposal = draftSession?.proposal as PlanProposal | null | undefined;
  // Solange die Prüferin prüft, ist der Vorschlag noch nicht fertig und lässt sich nicht übernehmen.
  const reviewPending = planProposal?.review?.state === "pending";
  const aiAdvice = aiAdviceOf(planProposal);
  const askPlan = useMutation({
    mutationFn: () => api.startPlanDraft(wo.id),
    onSuccess: async () => {
      toast("Die KI erstellt einen Planvorschlag.");
      await queryClient.invalidateQueries({ queryKey: ["studio"] });
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });
  const proposalKey = planProposal ? JSON.stringify(planProposal.items) : null;
  const adopted = proposalKey !== null && adoptedProposal === proposalKey;
  const adoptPlan = () => {
    if (!planProposal) return;
    setItems(toDraft(planProposal));
    setAdoptedProposal(proposalKey);
    toast("Planvorschlag übernommen. Prüfen, anpassen, dann als Revision speichern.");
  };
  // Beraterin übernommen: Jede Claude-Aufgabe hat schon Modell und Aufwand ihres Vorschlags.
  const adviceApplied = items
    .filter((it) => it.provider === "claude")
    .every((it) => {
      const a = adviceFor(it, spec, aiAdvice);
      return it.model === a.model && it.effort === a.effort;
    });
  const start = useMutation({
    mutationFn: () => api.start(wo.id, wo.version),
    onSuccess: async () => {
      toast("Auftrag gestartet. Der Node übernimmt die ersten Läufe.");
      await refresh();
    },
    onError: (err) => {
      // Zusätzlich als Meldung: auf dem Handy liegt das Formular weit über dem Knopf.
      setError(errorMessage(err));
      toast(errorMessage(err), "error");
    },
  });

  const update = (index: number, patch: Partial<DraftItem>) =>
    setItems((list) => list.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  const add = () =>
    setItems((list) => [
      ...list,
      {
        key: slug("aufgabe", new Set(list.map((i) => i.key))),
        lane: "code",
        title: "",
        criteria: "",
        dependsOn: [],
        provider: simulation ? "mock" : "claude",
        model: "",
        effort: "",
      },
    ]);
  const remove = (index: number) =>
    setItems((list) => {
      const removed = list[index]?.key;
      return list
        .filter((_, i) => i !== index)
        .map((it) => ({ ...it, dependsOn: it.dependsOn.filter((d) => d !== removed) }));
    });
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (items.length === 0) return setError("Mindestens eine Aufgabe anlegen.");
    if (items.some((i) => !i.title.trim() || lines(i.criteria).length === 0))
      return setError("Jede Aufgabe braucht Titel und mindestens ein Kriterium.");
    submit.mutate();
  };
  // Ein leerer Plan ist keine Änderung: Der Knopf leuchtet erst, wenn es etwas zu speichern gibt (auch nicht,
  // während die Beraterin noch plant).
  const empty = items.length === 0;
  const dirty = !empty && !sameContent(plan(), detail.plan?.content ?? null);

  return (
    <>
      {canAskPlan && (
        <div className="studio-ask">
          {draftSession ? (
            <StudioChat
              session={draftSession}
              title="Planvorschlag der KI"
              onRetry={() => askPlan.mutate()}
              waitText={
                reviewPending
                  ? "Die Prüferin prüft den Vorschlag auf Umfang, Modell und Aufwand. Das dauert meist unter einer Minute."
                  : undefined
              }
              proposalView={
                planProposal && !reviewPending ? (
                  <div className="studio-proposal">
                    <h3>Vorgeschlagene Aufgaben</h3>
                    {planProposal.review?.state === "done" && planProposal.review.summary && (
                      <p className="advice-review">
                        <strong>Prüferin:</strong> {planProposal.review.summary}
                      </p>
                    )}
                    {planProposal.review?.state === "failed" && (
                      <p className="muted" role="status">
                        Ungeprüft: {planProposal.review.error ?? "Die Prüferin konnte nicht prüfen."}
                      </p>
                    )}
                    <ol className="plan-proposal">
                      {planProposal.items.map((i) => (
                        <li key={i.key}>
                          <strong>{LANE_LABELS[i.lane]}:</strong> {i.title}
                          {i.model ? (
                            <span className="muted">
                              {" "}
                              ({MODEL_INFO[i.model].label}, Aufwand {i.effort})
                            </span>
                          ) : null}
                          {i.dependsOn.length > 0 && (
                            <span className="muted"> · nach {i.dependsOn.join(", ")}</span>
                          )}
                          <ReviewMark advice={planProposal.advice?.[i.key]} />
                        </li>
                      ))}
                    </ol>
                    <button
                      type="button"
                      className={adopted ? "btn btn--done" : "btn btn--primary"}
                      disabled={adopted}
                      onClick={adoptPlan}
                    >
                      <span className="btn-label">
                        <Icon name="check" />{" "}
                        {adopted ? "In den Plan übernommen" : "Vorschlag in den Plan übernehmen"}
                      </span>
                    </button>
                    {adopted && (
                      <p className="muted" role="status">
                        Steht unten im Plan. Prüfen, bei Bedarf anpassen, dann „Plan-Revision speichern“.
                      </p>
                    )}
                  </div>
                ) : undefined
              }
            />
          ) : (
            <button
              type="button"
              className="btn btn--primary"
              disabled={askPlan.isPending}
              onClick={() => askPlan.mutate()}
            >
              <span className="btn-label">
                <Icon name="bot" /> Plan von der KI vorschlagen lassen
              </span>
            </button>
          )}
        </div>
      )}
      <div className="stage stage--editor">
        <form className="panel form editor" onSubmit={onSubmit} noValidate aria-labelledby="planTitle">
          <div className="panel-head">
            <div>
              <h2 id="planTitle">Plan als Task-DAG</h2>
              <p className="muted">
                Jede Aufgabe gehört zu einer Spur. Abhängigkeiten bestimmen die Reihenfolge, Zyklen lehnt der
                Server ab.
              </p>
            </div>
            <span className="plan-head-actions">
              {items.some((i) => i.provider === "claude") && (
                <button
                  type="button"
                  className={adviceApplied ? "chip-btn is-done" : "chip-btn is-primary"}
                  disabled={adviceApplied}
                  onClick={applyAdvice}
                >
                  <Icon name="check" />{" "}
                  {adviceApplied
                    ? "Vorschläge der Beraterin übernommen"
                    : "Alle Vorschläge der Beraterin übernehmen"}
                </button>
              )}
              {simulation && (
                <button type="button" className="chip-btn" onClick={() => setItems(toDraft(DEMO_PLAN))}>
                  Demo-Plan einfügen
                </button>
              )}
            </span>
          </div>
          <ol className="plan-items">
            {items.map((it, index) => (
              <li
                key={it.key}
                className="plan-item"
                style={{
                  ["--accent" as string]: `var(--${it.lane === "research" ? "lime" : it.lane === "design" ? "orange" : it.lane === "code" ? "cyan" : it.lane === "test" ? "coral" : "violet"})`,
                }}
              >
                <div className="plan-row">
                  <label className="sr-only" htmlFor={`lane-${it.key}`}>
                    Spur
                  </label>
                  <select
                    id={`lane-${it.key}`}
                    value={it.lane}
                    onChange={(e) => update(index, { lane: e.target.value as Lane })}
                  >
                    {LANES.map((l) => (
                      <option key={l} value={l}>
                        {LANE_LABELS[l]}
                      </option>
                    ))}
                  </select>
                  <label className="sr-only" htmlFor={`title-${it.key}`}>
                    Titel
                  </label>
                  <input
                    id={`title-${it.key}`}
                    value={it.title}
                    placeholder="Titel der Aufgabe"
                    onChange={(e) => update(index, { title: e.target.value })}
                  />
                  <span className="mono muted">{it.key}</span>
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={`Aufgabe ${it.title || it.key} entfernen`}
                    onClick={() => remove(index)}
                  >
                    <Icon name="trash" />
                  </button>
                </div>
                <label htmlFor={`provider-${it.key}`}>Bearbeitet von</label>
                <select
                  id={`provider-${it.key}`}
                  value={it.provider}
                  onChange={(e) => update(index, { provider: e.target.value })}
                >
                  {providers.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
                {it.provider === "claude" && (
                  <ClaudeChoice
                    item={it}
                    spec={spec}
                    ai={aiAdvice}
                    onChange={(patch) => update(index, patch)}
                  />
                )}
                <label htmlFor={`crit-${it.key}`}>Akzeptanzkriterien (eine Zeile je Kriterium)</label>
                <textarea
                  id={`crit-${it.key}`}
                  rows={2}
                  value={it.criteria}
                  onChange={(e) => update(index, { criteria: e.target.value })}
                />
                {index > 0 && (
                  <fieldset className="deps">
                    <legend>Hängt ab von</legend>
                    {items.slice(0, index).map((dep) => (
                      <label key={dep.key} className="check">
                        <input
                          type="checkbox"
                          checked={it.dependsOn.includes(dep.key)}
                          onChange={(e) =>
                            update(index, {
                              dependsOn: e.target.checked
                                ? [...it.dependsOn, dep.key]
                                : it.dependsOn.filter((d) => d !== dep.key),
                            })
                          }
                        />
                        {dep.title || dep.key}
                      </label>
                    ))}
                  </fieldset>
                )}
              </li>
            ))}
          </ol>
          <ErrorNote error={error} />
          <div className="form-actions">
            <button type="button" className="btn" onClick={add}>
              <span className="btn-label">
                <Icon name="plus" /> Aufgabe
              </span>
            </button>
            {/* Ungespeicherte Änderungen: Knopf leuchtet; sonst zeigt er den gespeicherten Stand. */}
            <button
              type="submit"
              className={dirty ? "btn btn--primary" : empty ? "btn" : "btn btn--done"}
              disabled={submit.isPending || !dirty}
            >
              <span className="btn-label">
                <Icon name={dirty || empty ? "network" : "check"} />{" "}
                {submit.isPending
                  ? "Wird gespeichert …"
                  : dirty
                    ? "Plan-Revision speichern"
                    : empty
                      ? "Noch keine Aufgabe"
                      : "Plan gespeichert"}
              </span>
            </button>
          </div>
          <p className="muted small">
            Claude Code arbeitet auf der Werkstatt in einer Sandbox. Ist dem Projekt ein Repo zugeordnet,
            endet der Lauf mit einem Pull Request. Codex und Gemini CLI folgen in M4.
          </p>
        </form>
        <aside className="panel decision" aria-label="Freigabe und Start">
          <div className="dec-top">
            <span className="dec-kicker">{wo.state === "READY" ? "Startbereit" : "Freigabe"}</span>
          </div>
          {wo.state === "READY" ? (
            <>
              <h2 className="dec-title">
                <Icon name="play" /> <span>Auftrag starten?</span>
              </h2>
              <p className="dec-text">
                Plan v{detail.plan?.revision} ist freigegeben. Budget {usd(wo.budgetUsd)}. Der Node holt die
                ersten startbereiten Aufgaben.
              </p>
              <ul className="binding">
                <li>
                  <Icon name="file" /> Spec {shortHash(wo.specHash)}
                </li>
                <li>
                  <Icon name="network" /> Plan {shortHash(wo.planHash)}
                </li>
              </ul>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => start.mutate()}
                disabled={start.isPending}
              >
                <span className="btn-label">
                  Starten <Icon name="arrow" />
                </span>
              </button>
              <small className="muted">Änderst du den Plan, verfällt die Freigabe.</small>
            </>
          ) : detail.plan && !dirty ? (
            <>
              <h2 className="dec-title">
                <Icon name="network" /> <span>Plan v{detail.plan.revision} freigeben?</span>
              </h2>
              <p className="dec-text">
                {detail.plan.content.items.length} Aufgaben, als zyklenfreier DAG geprüft.
              </p>
              <ul className="binding">
                <li>
                  <Icon name="commit" /> {shortHash(detail.plan.hash)}
                </li>
                <li>
                  <Icon name="lock" /> Rolle owner
                </li>
              </ul>
              {canApprove ? (
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => approve.mutate()}
                  disabled={approve.isPending}
                >
                  <span className="btn-label">
                    Plan freigeben <Icon name="arrow" />
                  </span>
                </button>
              ) : (
                <p className="muted">Nur der Owner kann den Plan freigeben.</p>
              )}
            </>
          ) : (
            <EmptyState
              icon="network"
              title="Ungespeicherte Änderungen"
              text="Speichere den Plan als Revision. Danach kann er freigegeben werden."
            />
          )}
        </aside>
      </div>
    </>
  );
}

/* ---------- Belege im Drawer ---------- */

const ITEM_STATE: Record<WorkItem["state"], [string, string, string]> = {
  not_started: ["unknown", "nicht begonnen", "commit"],
  running: ["running", "läuft", "activity"],
  unverified: ["unverified", "nicht geprüft", "alert"],
  verifying: ["running", "wird geprüft", "shield"],
  verified: ["verified", "verifiziert", "check"],
  failed: ["blocked", "fehlgeschlagen", "x"],
  blocked: ["blocked", "blockiert", "alert"],
  waived_by_human: ["unknown", "verzichtet", "check"],
};

/**
 * Wunsch des Owners (24.10.2026): Eine Karte der Flow Map zeigt nicht nur Infos, sondern führt dorthin, wo man
 * handelt. Je Stufe höchstens zwei Ziele, das wichtigste zuerst.
 */
function FlowNext({ stage, detail }: { stage: string; detail: WorkOrderDetail }) {
  const wo = detail.workOrder;
  const pending = detail.decisions.some((x) => x.state === "PENDING");
  const toProject = (label: string) => (
    <Link key="project" to="/projects/$projectId" params={{ projectId: detail.project.id }} className="btn">
      {label}
    </Link>
  );
  const toTodo = (
    <Link key="todo" to="/" hash="fuer-dich" className="btn btn--primary">
      Zu „Für dich zu tun“ ({detail.openOwnerSteps})
    </Link>
  );
  const links: ReactNode[] = [];
  if (pending && (stage === "gate" || stage === "pr" || stage === "prod")) {
    links.push(
      <Link key="decision" to="/decisions" className="btn btn--primary">
        Zur Entscheidung
      </Link>,
    );
  }
  if (stage === "prod" && wo.awaitingRolloutSha) {
    links.push(
      <Link key="rollout" to="/" className="btn btn--primary">
        Ausrollen im Cockpit
      </Link>,
    );
  }
  if ((stage === "live" || stage === "prod") && detail.openOwnerSteps > 0) links.push(toTodo);
  if (stage === "live" || stage === "order" || stage === "brief") links.push(toProject("Zur Projektseite"));
  if (links.length === 0) return null;
  return (
    <section className="flow-next" aria-label="Weiter">
      <h3>Weiter</h3>
      <div className="form-actions">{links.slice(0, 2)}</div>
    </section>
  );
}

function FlowDrawer({
  target,
  detail,
  onClose,
}: {
  target: FlowTarget;
  detail: WorkOrderDetail;
  onClose: () => void;
}) {
  const wo = detail.workOrder;
  if (target.kind === "lane") {
    const items = detail.items.filter((i) => i.lane === target.lane);
    const runs = detail.runs.filter((r) => items.some((i) => i.id === r.workItemId));
    return (
      <Drawer
        title={LANE_LABELS[target.lane]}
        sub={`${wo.title} · ${detail.project.title}`}
        icon={LANE_ICON[target.lane]}
        onClose={onClose}
      >
        <section>
          <h3>Belege</h3>
          <dl className="kv">
            <dt>Aufgabenquote</dt>
            <dd>
              {items.filter((i) => i.state === "verified" || i.state === "waived_by_human").length} von{" "}
              {items.length} verifiziert
            </dd>
            <dt>Läufe</dt>
            <dd>{runs.length}</dd>
            <dt>Kosten</dt>
            <dd>{laneCostText(runs)}</dd>
            <dt>Provider</dt>
            <dd>{[...new Set(runs.map(runProvider))].join(", ") || "noch keiner"}</dd>
          </dl>
        </section>
        <section>
          <h3>WorkItems</h3>
          <ul className="items">
            {items.map((i) => {
              const last = detail.runs.find((r) => r.workItemId === i.id);
              // „läuft“ nur mit aktivem Lauf; ein Lauf in der Warteschlange wartet.
              const [cls, label, icon] =
                i.state === "running" && last?.state === "QUEUED"
                  ? ["unknown", "wartet", "clock"]
                  : ITEM_STATE[i.state];
              return (
                <li key={i.id}>
                  <span
                    className={`ic ${cls === "verified" ? "ok" : cls === "running" ? "run" : cls === "blocked" ? "blk" : "unk"}`}
                  >
                    <Icon name={icon} />
                  </span>
                  <span>
                    {displayTitle(i.title)}
                    {last && (
                      <small className="muted block">
                        Versuch {last.attempt} · {last.state.toLowerCase()}
                        {AUTH_MODE_LABEL[last.authMode] ? ` · ${AUTH_MODE_LABEL[last.authMode]}` : ""}{" "}
                        {last.resultSummary ? `· ${last.resultSummary}` : ""}
                        {last.failureReason ? `· ${readableReason(last.failureReason)}` : ""}
                      </small>
                    )}
                    {i.evidence.length > 0 && (
                      <small className="muted block mono">{i.evidence.slice(-2).join(" · ")}</small>
                    )}
                  </span>
                  <span className={`state state--${cls}`}>{label}</span>
                </li>
              );
            })}
          </ul>
        </section>
        <p className="muted small">
          Läufe mit Claude Code arbeiten im Repo des Projekts; belegt wird über die GitHub-Checks des Commits.
        </p>
      </Drawer>
    );
  }
  const labels: Record<string, [string, string]> = {
    order: ["Auftrag", "file"],
    orch: ["Orchestrator", "network"],
    gate: ["Quality Gate", "shield"],
    pr: ["Review", "pr"],
    staging: ["Staging", "server"],
    prod: ["Production", "rocket"],
    live: ["Live", "activity"],
    brief: ["Briefing", "file"],
  };
  const [title, icon] = labels[target.stage] ?? ["Stufe", "alert"];
  return (
    <Drawer title={title} sub={`${wo.title} · ${STATE_LABEL[wo.state]}`} icon={icon} onClose={onClose}>
      <FlowNext stage={target.stage} detail={detail} />
      <section>
        <h3>Belege</h3>
        <dl className="kv">
          <dt>Spec</dt>
          <dd>
            v{wo.specRevision} · {shortHash(wo.specHash)}
          </dd>
          <dt>Plan</dt>
          <dd>
            v{wo.planRevision} · {shortHash(wo.planHash)}
          </dd>
          <dt>Zustand</dt>
          <dd>
            {STATE_LABEL[wo.state]}
            {wo.state === "PAUSED_PROVIDER" && wo.pausedUntil
              ? `, weiter ab ${untilText(wo.pausedUntil)}`
              : ""}
          </dd>
          {wo.stateReason && (wo.state === "PAUSED_PROVIDER" || wo.state === "BLOCKED") && (
            <>
              <dt>Grund</dt>
              <dd>{readableReason(wo.stateReason)}</dd>
            </>
          )}
          <dt>Budget</dt>
          <dd>
            {usd(detail.cost.measuredUsd + detail.cost.estimatedUsd)} von {usd(detail.cost.budgetUsd)}
            {detail.runs.some((r) => r.authMode === "subscription") && (
              <small className="muted block">Abo-Läufe zählen nicht auf das Budget.</small>
            )}
          </dd>
          {target.stage === "prod" && (
            <>
              <dt>Auslieferung</dt>
              <dd>
                {wo.awaitingRolloutSha
                  ? `Gemergt (${wo.awaitingRolloutSha.slice(0, 7)}), noch nicht ausgerollt. Im Cockpit „Jetzt ausrollen“ halten.`
                  : "Git-Broker mergt den geprüften Stand nach deiner Freigabe. Ob er danach live ist, hängt vom Auslieferungsweg des Projekts ab."}
              </dd>
            </>
          )}
          {target.stage === "live" && (
            <>
              <dt>Live</dt>
              <dd>
                <strong>{detail.live.sub}</strong>
                <small className="muted block">{detail.live.detail}</small>
              </dd>
            </>
          )}
          {target.stage === "gate" && (
            <>
              <dt>Regel</dt>
              <dd>Agenten-Aussagen zählen nicht als Beleg</dd>
            </>
          )}
        </dl>
      </section>
      {target.stage === "orch" && detail.plan && (
        <section>
          <h3>Plan v{detail.plan.revision}</h3>
          <ul className="items">
            {detail.plan.content.items.map((i) => (
              <li key={i.key}>
                <span className="ic unk">
                  <Icon name={LANE_ICON[i.lane]} />
                </span>
                <span>
                  {displayTitle(i.title)}
                  {i.dependsOn.length > 0 && (
                    <small className="muted block">nach {i.dependsOn.join(", ")}</small>
                  )}
                </span>
                <span className="state state--unknown">{LANE_LABELS[i.lane]}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </Drawer>
  );
}

/* ---------- Seite ---------- */

const PLANNING = new Set(["DRAFT", "SPEC_REVIEW", "PLAN_REVIEW", "READY"]);

export function WorkOrderPage({ orderId, auth }: { orderId: string; auth: AuthStatus }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const detail = useQuery({ queryKey: ["work-order", orderId], queryFn: () => api.workOrder(orderId) });
  const events = useQuery({ queryKey: ["wo-events", orderId], queryFn: () => api.workOrderEvents(orderId) });
  const usage = useQuery({ queryKey: ["usage"], queryFn: api.usage });
  const [target, setTarget] = useState<FlowTarget | null>(null);
  const onOpen = useCallback((t: FlowTarget) => setTarget(t), []);
  const cancel = useMutation({
    mutationFn: () => api.cancel(orderId, "Vom Menschen abgebrochen"),
    onSuccess: async () => {
      toast("Auftrag abgebrochen, Läufe gestoppt");
      await queryClient.invalidateQueries();
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });

  // Schon umgesetzt (QA-08): ohne Auslieferung abschließen; taskFLOW meldet die Aufgabe als erledigt.
  const alreadyDone = useMutation({
    mutationFn: () => api.markAlreadyDone(orderId),
    onSuccess: async () => {
      toast("Als schon umgesetzt abgeschlossen. Ein offener Pull Request wird geschlossen.");
      await queryClient.invalidateQueries();
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });
  // Anhalten, Fortsetzen und Vorziehen (UX-10): laufende Arbeit endet regulär, Neues startet erst danach.
  const hold = useMutation({
    mutationFn: (h: boolean) => api.holdWorkOrder(orderId, h),
    onSuccess: async (w) => {
      toast(w.heldAt ? "Angehalten. Ein laufender Schritt endet noch regulär." : "Fortgesetzt.");
      await queryClient.invalidateQueries({ queryKey: ["work-order", orderId] });
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });
  const prioritize = useMutation({
    mutationFn: () => api.prioritizeWorkOrder(orderId),
    onSuccess: async () => {
      toast("Vorgezogen. Die nächsten Läufe dieses Auftrags kommen zuerst dran.");
      await queryClient.invalidateQueries({ queryKey: ["work-order", orderId] });
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });

  const flow = useMemo(
    () =>
      detail.data
        ? projectFlow(detail.data.workOrder, detail.data.items, detail.data.decisions, {
            runs: detail.data.runs,
            studioActive: detail.data.studioActive,
          })
        : null,
    [detail.data],
  );

  if (detail.isError) {
    return (
      <EmptyState
        icon="alert"
        title="Auftrag nicht gefunden"
        text={errorMessage(detail.error)}
        action={<Link to="/">Zum Cockpit</Link>}
      />
    );
  }
  if (!detail.data || !flow) return <p className="muted">Lade Auftrag …</p>;
  const d = detail.data;
  const wo = d.workOrder;
  const runCounts = countRuns(d.runs.map((r) => r.state));
  const status = orderStatus({
    ...wo,
    activeRuns: runCounts.activeRuns + (d.studioActive ? 1 : 0),
    queuedRuns: runCounts.queuedRuns,
    openOwnerSteps: d.openOwnerSteps,
  });
  const isOwner = auth.user?.role === "owner";
  const decision = flow.openDecisions.find((x) => x.category === "release") ?? flow.openDecisions[0];
  const costRows = costRowsOf(d.runs);
  const terminal = ["RELEASED", "CANCELLED", "FAILED"].includes(wo.state);
  // Echte Läufe, sobald eine Aufgabe nicht mit dem Mock-Agenten arbeitet.
  const realRuns = d.items.some((i) => i.providerProfile !== "mock");

  return (
    <>
      <section className="hero" aria-labelledby="heroTitle">
        <div className="hero-text">
          <p className="eyebrow">
            <Link to="/projects/$projectId" params={{ projectId: d.project.id }} className="chip chip--ghost">
              {d.project.title}
            </Link>
            <span className="mono">
              {wo.id.slice(0, 11)} · Spec v{wo.specRevision} · Plan v{wo.planRevision}
            </span>
            <span className={`chip state-chip state-${status.cls}`}>{status.label}</span>
            {d.taskflow ? (
              <a
                className="chip chip--ghost"
                href={d.taskflow.remoteUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Aus taskFLOW${d.taskflow.requestedBy ? `, gesendet von ${d.taskflow.requestedBy}` : ""}: Task öffnen (neues Fenster)`}
              >
                aus taskFLOW
              </a>
            ) : null}
          </p>
          <h1 id="heroTitle">
            {wo.title}: vom Briefing zum <span className="glow-word">Release.</span>
          </h1>
        </div>
        <Stepper steps={flow.steps} />
      </section>

      <StateNotice state={wo.state} reason={wo.stateReason} pausedUntil={wo.pausedUntil} />

      {wo.state === "DRAFT" || wo.state === "SPEC_REVIEW" ? (
        <SpecEditor key={`spec-${d.spec?.revision ?? 0}`} detail={d} canApprove={isOwner} />
      ) : wo.state === "PLAN_REVIEW" || wo.state === "READY" ? (
        <PlanEditor key={`plan-${d.plan?.revision ?? 0}-${wo.state}`} detail={d} canApprove={isOwner} />
      ) : (
        <>
          {decision && (
            // Was dich braucht, steht über der Flow Map (UX-Umbau, Phase 3).
            <aside className="panel decision decision--top" aria-label="Entscheidung">
              <DecisionCard key={decision.id} decision={decision} />
            </aside>
          )}
          <section className={`stage${decision ? " stage--solo" : ""}`}>
            <div className="panel flow" id="flow" aria-labelledby="flowTitle">
              <div className="flow-head">
                <h2 id="flowTitle" className="sr-only">
                  Flow Map des Auftrags
                </h2>
                <div className="flow-head-left">
                  <span className={`chip ${realRuns ? "" : "chip--sample"}`}>
                    {realRuns ? "Claude Code · echte Läufe" : "Mock-Agent · Probelauf"}
                  </span>
                  {wo.heldAt && <span className="chip is-held">Angehalten</span>}
                  {wo.gitPrUrl && (
                    <a className="chip-btn" href={wo.gitPrUrl} target="_blank" rel="noreferrer noopener">
                      <Icon name="pr" /> Pull Request
                    </a>
                  )}
                  {!terminal && !PLANNING.has(wo.state) && (
                    <>
                      <button
                        type="button"
                        className="chip-btn"
                        onClick={() => hold.mutate(!wo.heldAt)}
                        disabled={hold.isPending}
                      >
                        <Icon name={wo.heldAt ? "play" : "clock"} /> {wo.heldAt ? "Fortsetzen" : "Anhalten"}
                      </button>
                      <button
                        type="button"
                        className="chip-btn"
                        onClick={() => prioritize.mutate()}
                        disabled={prioritize.isPending}
                      >
                        <Icon name="arrow" /> Vorziehen
                      </button>
                      {isOwner && (
                        <button
                          type="button"
                          className="chip-btn"
                          onClick={() => {
                            if (
                              window.confirm(
                                "Ist der Auftrag schon umgesetzt? Er wird ohne Auslieferung abgeschlossen, ein offener Pull Request wird geschlossen.",
                              )
                            ) {
                              alreadyDone.mutate();
                            }
                          }}
                          disabled={alreadyDone.isPending}
                        >
                          <Icon name="check" /> Schon erledigt
                        </button>
                      )}
                      <button
                        type="button"
                        className="chip-btn is-danger"
                        onClick={() => cancel.mutate()}
                        disabled={cancel.isPending}
                      >
                        <Icon name="x" /> Abbrechen
                      </button>
                    </>
                  )}
                </div>
                <div className="flow-legend" aria-hidden="true">
                  <span>
                    <i className="lg lg--done" />
                    verifiziert
                  </span>
                  <span>
                    <i className="lg lg--run" />
                    läuft
                  </span>
                  <span>
                    <i className="lg lg--wait" />
                    wartet
                  </span>
                  <span>
                    <i className="lg lg--block" />
                    blockiert
                  </span>
                </div>
              </div>
              <FlowMap
                flow={flow}
                title={d.project.title}
                goal={d.project.goal}
                meta={`Spec v${wo.specRevision} · Plan v${wo.planRevision} · Budget ${usd(wo.budgetUsd)}`}
                tags={[d.project.platform.replace("_", " "), d.project.profileKey]}
                paused={
                  wo.state === "PAUSED_PROVIDER"
                    ? wo.pausedUntil
                      ? `weiter ab ${untilText(wo.pausedUntil)}`
                      : "Rücksetzzeit offen"
                    : null
                }
                live={d.live}
                onOpen={onOpen}
              />
            </div>
            {!decision && (
              <aside className="panel decision" aria-label="Entscheidung">
                <EmptyState
                  icon={wo.state === "RELEASED" || wo.awaitingRolloutSha ? "rocket" : "inbox"}
                  title={
                    wo.state === "RELEASED"
                      ? "Ausgeliefert"
                      : wo.awaitingRolloutSha
                        ? "Gemergt, noch nicht ausgerollt"
                        : "Gerade nichts zu entscheiden"
                  }
                  text={
                    wo.state === "RELEASED"
                      ? "Dieser Auftrag ist abgeschlossen. Alle Belege findest du in der Timeline."
                      : wo.awaitingRolloutSha
                        ? "Sichtbar wird die Änderung, sobald das Büro den neuen Stand läuft. Halte dafür im Cockpit „Jetzt ausrollen“."
                        : "Die Agenten arbeiten. Sobald eine Rückfrage oder ein Gate kommt, erscheint es hier."
                  }
                />
              </aside>
            )}
          </section>
        </>
      )}

      {/* Selten gebraucht, darum zugeklappt mit Zweck im Titel (UX-Umbau, Phase 3). */}
      <details className="panel fold history-fold">
        <summary>
          <h2>Verlauf</h2> <span className="muted">jeder Schritt mit Belegen, zur Fehlersuche</span>
        </summary>
        <section className="bottom bottom--two">
          <Timeline events={events.data ?? []} />
          <Activity events={events.data ?? []} />
        </section>
      </details>
      <details className="panel fold costs-fold">
        <summary>
          <h2>Kosten und Verbrauch</h2> <span className="muted">Budget, Läufe und Token dieses Auftrags</span>
        </summary>
        <CostRing rows={costRows} budgetUsd={wo.budgetUsd} title="Kosten des Auftrags" />
        <WorkOrderTokens
          usage={usage.data?.projects.flatMap((p) => p.workOrders).find((w) => w.workOrderId === wo.id)}
        />
      </details>

      {target && <FlowDrawer target={target} detail={d} onClose={() => setTarget(null)} />}
    </>
  );
}
